"use client";

import { useRef, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Captions } from "lucide-react";

const VIDEO_URL = "/videos/caption_example.mp4";

const CaptionSkillExplained = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hasPlayed, setHasPlayed] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;

    if (!video || !container || hasPlayed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasPlayed) {
            video.play().catch(() => {});
            setHasPlayed(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.8 }
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, [hasPlayed]);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.loop = true;
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.loop = false;
    }
  };

  return (
    <section className="pt-4 container max-w-6xl mx-auto px-4">
      <div>
        <div className="flex-row-reverse lg:flex">
          <div className="lg:w-1/2">
            <div className="mb-0">
              <div
                ref={containerRef}
                className="relative block overflow-hidden rounded-2xl leading-none"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  className="aspect-video w-full object-cover rounded-2xl"
                  poster="/videos/caption_example-cover.jpeg"
                >
                  <source src={VIDEO_URL} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
          <div className="lg:flex lg:w-1/2 lg:items-center lg:pr-24 2xl:pr-32 mt-6 lg:mt-0">
            <div>
              <Badge
                variant="outline"
                className="mb-4 w-fit gap-1 bg-card px-3 text-sm font-normal tracking-tight shadow-sm"
              >
                <Captions className="size-4" />
                Three lengths
              </Badge>
              <h3 className="mb-3 text-xl font-semibold md:mb-4 md:text-4xl lg:mb-6">
                Rich descriptions, no prompts required
              </h3>
              <p className="text-muted-foreground lg:text-lg">
                Process millions of images with sub-400ms latency. Three length options—<strong className="text-foreground">short</strong>, <strong className="text-foreground">normal</strong>, and <strong className="text-foreground">long</strong>—let you balance speed and detail for any workflow.
              </p>
              <p className="text-muted-foreground lg:text-lg mt-4">
                Whether you&apos;re labeling a media library, generating training data, or building accessibility features, Moondream Caption delivers consistent quality at scale without breaking the bank.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { CaptionSkillExplained };
